<script setup>
import { computed } from 'vue';
import {
  format,
  fromUnixTime,
  isThisYear,
  isToday,
  isYesterday,
} from 'date-fns';
import { getLastMessage } from 'dashboard/helper/conversationHelper';
import CardContent from './CardContent.vue';
import Avatar from 'next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { getInboxIconByType } from 'dashboard/helper/inbox';

const props = defineProps({
  chat: { type: Object, required: true },
  currentContact: { type: Object, required: true },
  assignee: { type: Object, default: () => ({}) },
  inbox: { type: Object, default: () => ({}) },
  accountLabels: { type: Array, default: () => [] },
  selected: { type: Boolean, default: false },
  isActiveChat: { type: Boolean, default: false },
  showAssignee: { type: Boolean, default: false },
  showInboxName: { type: Boolean, default: false },
  isInboxView: { type: Boolean, default: false },
});

defineEmits(['click', 'contextmenu']);

const lastMessageInChat = computed(() => getLastMessage(props.chat));

const inboxIcon = computed(() => {
  const { channelType, medium, voiceEnabled } = props.inbox;
  return getInboxIconByType(channelType, medium, 'fill', voiceEnabled);
});

const inboxDisplayName = computed(() => {
  if (props.inbox.name) return props.inbox.name;
  return props.showInboxName && !props.isInboxView
    ? 'Caixa de entrada'
    : 'Canal';
});

const labelSummary = computed(() => {
  const labelTitles = Array.isArray(props.chat.labels) ? props.chat.labels : [];
  if (!labelTitles.length) return null;

  const firstLabel = props.accountLabels.find(
    label => label.title === labelTitles[0]
  );

  return {
    name: firstLabel?.title || labelTitles[0],
    color: firstLabel?.color || '#64748b',
    remaining: labelTitles.length - 1,
  };
});

const emptyLabel = 'Sem etiqueta';

const labelCounter = computed(() => {
  const remaining = labelSummary.value?.remaining || 0;
  return remaining ? `+${remaining}` : '';
});

const voiceCallData = computed(() => {
  const last = lastMessageInChat.value;
  if (last?.content_type !== 'voice_call' || !last.call) {
    return { status: null, direction: null };
  }
  return {
    status: last.call.status,
    direction: last.call.direction === 'outgoing' ? 'outbound' : 'inbound',
  };
});

const unreadCount = computed(() => props.chat.unread_count);

const lastMessageDateTime = computed(() => {
  const timestamp = lastMessageInChat.value?.created_at || props.chat.timestamp;
  if (!timestamp) return 'Sem registro';

  const date = fromUnixTime(Number(timestamp));
  if (Number.isNaN(date.getTime())) return 'Sem registro';

  if (isToday(date)) return `Hoje, ${format(date, 'HH:mm')}`;
  if (isYesterday(date)) return `Ontem, ${format(date, 'HH:mm')}`;
  if (isThisYear(date)) return format(date, 'dd/MM, HH:mm');

  return format(date, 'dd/MM/yyyy, HH:mm');
});
</script>

<template>
  <div
    class="conversation relative grid min-h-[5.25rem] cursor-pointer grid-cols-[minmax(13rem,1.2fr)_minmax(8rem,.8fr)_minmax(18rem,1.9fr)_minmax(9rem,.85fr)_minmax(10rem,.9fr)_6rem] items-center gap-4 border-b border-n-slate-4 px-5 py-3 text-[0.9375rem] even:bg-n-slate-2/40 hover:z-[1] hover:border-n-surface-1 hover:bg-n-alpha-1 dark:border-[#222222] dark:even:bg-[#141414]/30 dark:hover:bg-[#1a1a1a]"
    :class="{
      'active animate-card-select bg-n-alpha-1 dark:bg-[#1a1a1a] !border-n-surface-1 dark:!border-[#2e2e2e]':
        isActiveChat,
      'selected bg-n-slate-2 dark:bg-[#1c1c1c] !border-n-surface-1 dark:!border-[#2e2e2e]':
        selected,
    }"
    @click="$emit('click', $event)"
    @contextmenu="$emit('contextmenu', $event)"
  >
    <div class="flex min-w-0 items-center gap-2">
      <span
        class="size-2.5 shrink-0 rounded-full"
        :class="
          Number(unreadCount) > 0
            ? 'bg-emerald-500 shadow-[0_0_0_3px_rgba(16,185,129,0.22)]'
            : 'bg-transparent'
        "
        :title="Number(unreadCount) > 0 ? 'Nova mensagem' : undefined"
        :aria-label="Number(unreadCount) > 0 ? 'Nova mensagem' : undefined"
        :aria-hidden="Number(unreadCount) <= 0"
      />
      <Avatar
        :name="currentContact.name"
        :src="currentContact.thumbnail"
        :size="28"
        :status="currentContact.availability_status"
        hide-offline-status
      />
      <div class="min-w-0">
        <h4 class="m-0 truncate font-medium text-n-slate-12">
          {{ currentContact.name }}
        </h4>
        <span class="flex items-center gap-0.5 text-xs text-n-slate-10">
          <Icon icon="i-woot-hash" class="size-3" />
          {{ chat.id }}
        </span>
      </div>
    </div>

    <div class="flex min-w-0 items-center gap-1.5 text-n-slate-11">
      <Icon :icon="inboxIcon" class="size-4 flex-shrink-0" />
      <span class="truncate" :title="inboxDisplayName">
        {{ inboxDisplayName }}
      </span>
    </div>

    <CardContent
      class="min-w-0"
      :last-message="lastMessageInChat"
      :voice-call-status="voiceCallData.status"
      :voice-call-direction="voiceCallData.direction"
      :unread-count="unreadCount"
      :show-unread-badge="false"
      :show-expanded-preview="false"
    />

    <div
      class="flex min-w-0 items-center gap-1.5 text-n-slate-11"
      :title="chat.attending_agent_names || 'Nenhuma resposta pública enviada'"
    >
      <Icon icon="i-lucide-users" class="size-4 shrink-0 text-n-slate-8" />
      <span class="truncate">
        {{ chat.attending_agent_names || 'Sem resposta de agente' }}
      </span>
    </div>

    <div class="min-w-0">
      <span
        v-if="labelSummary"
        class="inline-flex max-w-full items-center gap-1.5 truncate rounded-full border bg-n-slate-2 px-2.5 py-1 text-xs font-medium text-n-slate-12"
        :style="{ borderColor: labelSummary.color }"
        :title="chat.labels.join(', ')"
      >
        <span
          class="size-2 shrink-0 rounded-full"
          :style="{ backgroundColor: labelSummary.color }"
        />
        <span class="truncate">{{ labelSummary.name }}</span>
        <span v-if="labelCounter" class="shrink-0 text-n-slate-10">
          {{ labelCounter }}
        </span>
      </span>
      <span v-else class="text-xs text-n-slate-9">{{ emptyLabel }}</span>
    </div>

    <span class="text-right text-xs font-medium text-n-slate-11">
      {{ lastMessageDateTime }}
    </span>
  </div>
</template>
